import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import axios from "axios";
import { baseURL } from "../../API/API";
import Header from "../../Components/common/Header";
import Footer from "../../Components/common/Footer";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import BundleContentAccordion from "./BundleContentAccordion";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCalendarDays, faCoins, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { getCoursePackageTypeLabel } from "../../utils/packageLabels";
import "./HomePage.css";

export default function CoursePackageDetail() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const client = auth?.accessToken ? axiosPrivate : axios;
    client.get(`${baseURL}/learning/packages/detail/${packageId}`)
      .then((res) => setPkg(res.data?.data))
      .catch(() => toast.error("فشل تحميل تفاصيل الباقة"))
      .finally(() => setLoading(false));
  }, [auth?.accessToken, axiosPrivate, packageId]);

  if (loading) return <><Header /><div className="text-center py-5 text-muted"><h3>جاري تحميل تفاصيل الباقة...</h3></div><Footer /></>;
  if (!pkg) return <><Header /><div className="text-center py-5 text-muted"><h3>الباقة غير موجودة</h3></div><Footer /></>;

  const finalPrice = Math.max(Number(pkg.price || 0) - Number(pkg.discount || 0), 0);
  const singleTotal = (pkg.chapters || []).reduce((sum, ch) => sum + Number(ch.price || 0), 0);
  const packageLabel = getCoursePackageTypeLabel(pkg);

  const subscribe = () => {
    if (!auth?.accessToken) {
      toast.info("يرجى تسجيل الدخول أو إنشاء حساب للاشتراك");
      navigate("/register");
      return;
    }
    if (!auth.user?.is_staff && Number(auth.user?.academic_year_id) !== Number(pkg.academic_year_id)) {
      toast.error("هذه الباقة مخصصة لسنة دراسية مختلفة عن سنة حسابك.");
      return;
    }
    navigate(`/invoice/package/${pkg.id}`);
  };

  return (
    <div dir="rtl" style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <Header />
      <div className="learn-purple-banner py-5" style={{ background: "linear-gradient(135deg, #4c0d16 0%, #1f7a6f 100%)", color: "#fff" }}>
        <Container>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-4">
            <div className="text-end">
              <span className="badge bg-warning text-dark mb-3 px-3 py-2 fs-6">{packageLabel}</span>
              <h1 className="fw-bold mb-3">{pkg.name}</h1>
              <p className="mb-2 opacity-90" style={{ maxWidth: 800, fontSize: "1.05rem", lineHeight: 1.8 }}>
                {pkg.description || `اشتراك ${packageLabel} مع ${pkg.teacher_name} لسنة ${pkg.academic_year_name}`}
              </p>
            </div>
            <Link to="/" className="btn text-white rounded-pill px-4 d-flex align-items-center gap-2 text-decoration-none" style={{ border: "1.5px solid #fff" }}>
              <span>العودة للرئيسية</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </Container>
      </div>

      <Container className="my-5">
        <Row className="g-4">
          <Col lg={8}>
            <BundleContentAccordion
              chapters={pkg.chapters || []}
              hasAccess={pkg.has_accessed}
              title="شهور ودروس الباقة"
              returnTo={`/packages/${pkg.id}`}
              returnLabel="العودة للباقة"
            />

            <Card className="border shadow-sm rounded-4 p-4 text-end mt-4">
              <h4 className="fw-bold text-dark mb-3">وصف وتفاصيل الباقة</h4>
              <p className="text-muted" style={{ lineHeight: "1.8", fontSize: "1rem" }}>
                {pkg.description || `هذا الاشتراك يتيح لك فتح ودراسة الشهور التعليمية المحددة في الباقة مع الأستاذ ${pkg.teacher_name} لصف ${pkg.academic_year_name} بخصم مميز وخاص.`}
              </p>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="border shadow-sm rounded-4 p-4 text-center sticky-top" style={{ top: 100, zIndex: 10 }}>
              {pkg.image && (
                <div className="mb-4 rounded-3 overflow-hidden" style={{ height: 200 }}>
                  <img src={pkg.image} alt={pkg.name} className="w-100 h-100 object-fit-cover" />
                </div>
              )}
              
              <h4 className="fw-bold text-dark mb-4">تفاصيل الاشتراك بالباقة</h4>
              <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                <span className="badge bg-warning text-dark px-3 py-2 fs-6">{packageLabel}</span>
                <span className="badge bg-light text-dark border px-3 py-2 fs-6">{pkg.teacher_name}</span>
              </div>
              <p className="text-muted small mb-4">{pkg.academic_year_name}</p>

              <div className="bg-light p-4 rounded-4 mb-4">
                <div className="text-muted small mb-2">إجمالي الشهور منفردة:</div>
                <div className="text-decoration-line-through text-muted mb-3 fs-5">{singleTotal.toFixed(0)} جنيه مصري</div>
                <div className="text-muted small mb-1">سعر الباقة الموحد بعد الخصم:</div>
                <div className="fs-2 fw-bold text-primary mb-2">{finalPrice.toFixed(0)} جنيه</div>
                {Number(pkg.discount) > 0 && (
                  <span className="badge bg-danger-light text-danger px-3 py-2 rounded-pill small">
                    خصم باقة الترم/السنة: {Number(pkg.discount).toFixed(0)} جنيه
                  </span>
                )}
              </div>
              {pkg.has_accessed ? (
                <div className="alert alert-success d-flex align-items-center justify-content-center gap-2 rounded-pill mb-0">
                  <FontAwesomeIcon icon={faUnlock} />
                  <span className="fw-bold">أنت مشترك بالفعل، افتح الشهور هنا وابدأ الدرس من صفحة الدرس</span>
                </div>
              ) : (
                <Button onClick={subscribe} className="w-100 rounded-pill py-3 fw-bold fs-5 flex-center-both gap-2" style={{ backgroundColor: "var(--brand-300)", borderColor: "var(--brand-300)", color: "#fff" }}>
                  <FontAwesomeIcon icon={faCoins} />
                  <span>اشترك الآن في الباقة!</span>
                </Button>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
      <Footer />
    </div>
  );
}


